"use client";

// src/disclosure.tsx
import * as React8 from "react";

// src/primitives/disclosure-context.tsx
import * as React2 from "react";
import { useDisclosureState as useBaseDisclosureState } from "@stepstone-genesis/reakit-library";
import { useUID } from "@stepstone-genesis/uid-library";
import { useSafeEffect } from "@stepstone-genesis/hooks-use-safe-effect";

// src/primitives/disclosure-group-context.tsx
import * as React from "react";
var DisclosureGroupContext = React.createContext(null);
var noopContext = {
  disclosures: [],
  updateSelectedDisclosures: () => {
  },
  maximumVisible: "all"
};
var useDisclosureGroupContext = () => {
  const context = React.useContext(DisclosureGroupContext);
  return context != null ? context : noopContext;
};
var useGroupDisclosure = (maximumVisible = -1) => {
  const [disclosures, setDisclosures] = React.useState([]);
  const updateSelectedDisclosures = React.useCallback(
    (disclosure, visible) => {
      setDisclosures((prevState) => {
        const nextState = !visible ? prevState.filter((item) => item !== disclosure) : (
          // does it exist in the array if so return prevState or add new id on to prev state
          prevState.find((item) => item === disclosure) ? prevState : [...prevState, disclosure]
        );
        const length = nextState.length > maximumVisible;
        const [, ...rest] = nextState;
        return length ? rest : nextState;
      });
    },
    [maximumVisible]
  );
  return {
    disclosures,
    updateSelectedDisclosures
  };
};
var DisclosureGroupProvider = ({ maximumVisible = "all", isAnimatable = false, children }) => {
  const maxVisible = React.useMemo(() => {
    return maximumVisible === "all" ? -1 : maximumVisible;
  }, [maximumVisible]);
  const context = useGroupDisclosure(maxVisible);
  return /* @__PURE__ */ React.createElement(
    DisclosureGroupContext.Provider,
    {
      value: { ...context, maximumVisible, isAnimatable }
    },
    children
  );
};

// src/primitives/disclosure-context.tsx
var DisclosureContext = React2.createContext(
  null
);
var CustomDisclosureContext = React2.createContext(null);
var useCustomDisclosureContext = () => {
  const context = React2.useContext(CustomDisclosureContext);
  if (!context) {
    throw new Error(
      "useCustomDisclosureContext must be used within DisclosureProvider"
    );
  }
  return context;
};
var useDisclosureContext = () => {
  const context = React2.useContext(DisclosureContext);
  if (!context) {
    throw new Error(
      "useDisclosureContext must be used within DisclosureProvider"
    );
  }
  return context;
};
var DisclosureProvider = ({
  id: identifier,
  variant,
  isVisible,
  children,
  isAnimatable = false,
  ...initialState
}) => {
  const uuid = useUID();
  const id = identifier != null ? identifier : `genesis-disclosure-${uuid}`;
  const {
    disclosures,
    updateSelectedDisclosures,
    isAnimatable: isGroupContentAnimatable
  } = useDisclosureGroupContext();
  const isDisclosureAnimatable = isGroupContentAnimatable != null ? isGroupContentAnimatable : isAnimatable;
  const baseState = useBaseDisclosureState({
    baseId: id,
    visible: isVisible,
    animated: isDisclosureAnimatable,
    ...initialState
  });
  const originalState = React2.useMemo(
    () => ({
      ...baseState
    }),
    [baseState, isVisible]
  );
  const customState = React2.useMemo(
    () => ({
      isAnimatable: isDisclosureAnimatable,
      variant
    }),
    [variant, isDisclosureAnimatable]
  );
  const { visible, baseId, setVisible } = originalState;
  React2.useEffect(() => {
    setVisible(isVisible || false);
  }, [isVisible]);
  useSafeEffect(() => {
    return updateSelectedDisclosures(baseId, visible);
  }, [baseId, updateSelectedDisclosures, visible]);
  React2.useMemo(() => {
    if (!disclosures.length) return;
    if (!disclosures.includes(baseId)) return setVisible(false);
  }, [disclosures, baseId, setVisible]);
  return /* @__PURE__ */ React2.createElement(DisclosureContext.Provider, { value: originalState }, /* @__PURE__ */ React2.createElement(CustomDisclosureContext.Provider, { value: customState }, children));
};

// src/primitives/content.tsx
import * as React4 from "react";
import { Base, mergeStyles, systemForwardRef } from "@stepstone-genesis/system";
import { AnimatePresence, Motion } from "@stepstone-genesis/components-motion";
import { DisclosureContent as DisclosureContentPrimitive } from "@stepstone-genesis/reakit-library";

// src/utils.tsx
import * as React3 from "react";
import { hasChildren, isFunction } from "@stepstone-genesis/utilities";
var useClonedOutput = ({
  children,
  renderCustomElement,
  disclosure
}) => {
  const renderClonedOutput = React3.useCallback(
    ({ state, props }) => {
      if (isFunction(renderCustomElement)) {
        return renderCustomElement({ state, props });
      }
      if (hasChildren(children)) {
        return React3.Children.only(React3.cloneElement(children, props));
      }
      return React3.cloneElement(disclosure, props, children);
    },
    [children, disclosure, renderCustomElement]
  );
  return renderClonedOutput;
};

// src/animations.ts
var content = {
  visible: { opacity: 1, height: "auto" },
  collapsed: { opacity: 0, height: 0 }
};
var indicator = {
  visible: { transform: "rotate(180deg)" },
  collapsed: { transform: "rotate(0deg)" }
};
var animations = {
  content,
  indicator
};

// src/primitives/content.tsx
var DisclosureContent = systemForwardRef(
  function DisclosureContent2({
    as = "div",
    children,
    renderCustomContent,
    content: content2,
    __root,
    ...restProps
  }, ref) {
    var _a;
    const state = useDisclosureContext();
    const groupState = useDisclosureGroupContext();
    const customState = useCustomDisclosureContext();
    const isContentAnimatable = (_a = customState.isAnimatable) != null ? _a : groupState.isAnimatable;
    const renderClonedOutput = useClonedOutput({
      children,
      renderCustomElement: renderCustomContent,
      disclosure: isContentAnimatable ? /* @__PURE__ */ React4.createElement(Motion, { as: content2 != null ? content2 : as }) : content2 != null ? content2 : /* @__PURE__ */ React4.createElement(Base, { as })
    });
    const animateContent = React4.useMemo(() => {
      return isContentAnimatable ? {
        variants: animations.content,
        initial: "collapsed",
        animate: "visible",
        exit: "collapsed",
        transition: {
          duration: 0.25,
          ease: [0.04, 0.62, 0.23, 0.98]
        },
        onAnimationComplete: state.stopAnimation
      } : {};
    }, [isContentAnimatable, state.stopAnimation]);
    if (!state) {
      return null;
    }
    return /* @__PURE__ */ React4.createElement(DisclosureContentPrimitive, { ...state, ...restProps, ref }, ({ id, ...disclosureContentProps }) => isContentAnimatable ? (
      /**
       * in animated mode we do not want display none. Instead we replace it with aria-hidden.
       * and keep display as `initial` this keeps animation smooth
       * Please note: To keep accessible we also de-construct the id from the returned props as we do not want double the ids on the wrapper and the inner content
       */
      /* @__PURE__ */ React4.createElement(
        Base,
        {
          ...disclosureContentProps,
          __root: mergeStyles([{ display: "initial" }, __root]),
          ...!state.visible && { "aria-hidden": !state.visible },
          id,
          style: {}
        },
        /* @__PURE__ */ React4.createElement(AnimatePresence, { initial: false }, state.visible && /* @__PURE__ */ React4.createElement(
          Motion,
          {
            key: "content",
            __root: { overflow: "hidden" },
            ...animateContent
          },
          renderClonedOutput({
            state,
            props: { ...disclosureContentProps, ...restProps }
          })
        ))
      )
    ) : renderClonedOutput({
      state,
      props: { id, ...disclosureContentProps, __root, ...restProps }
    }));
  }
);

// src/primitives/trigger.tsx
import * as React5 from "react";
import {
  Base as Base2,
  systemForwardRef as systemForwardRef2,
  useMergeStyles
} from "@stepstone-genesis/system";
import { Disclosure as DisclosurePrimitive } from "@stepstone-genesis/reakit-library";
import { styles as textStyles } from "@stepstone-genesis/components-text";

// src/styles.ts
var trigger = {
  cursor: "pointer",
  _focusVisible: {
    outline: "outlineDefault",
    outlineOffset: "2px",
    boxShadow: "boxShadowFocusWhite"
  },
  _disabled: {
    cursor: "not-allowed",
    boxShadow: "boxShadowNone",
    color: "colorGreyLight"
  }
};
var indicator2 = {
  pointerEvents: "none",
  _focus: { boxShadow: "boxShadowNone" },
  display: "flex"
};
var styles = {
  trigger,
  indicator: indicator2
};

// src/primitives/trigger.tsx
var DisclosureTrigger = systemForwardRef2(
  function DisclosureTrigger2(props, ref) {
    const {
      as = "button",
      children,
      renderCustomTrigger,
      disclosure = /* @__PURE__ */ React5.createElement(Base2, { as, xs: textStyles.root }),
      __root,
      ...restProps
    } = props;
    const state = useDisclosureContext();
    const renderClonedOutput = useClonedOutput({
      children,
      renderCustomElement: renderCustomTrigger,
      disclosure
    });
    const triggerStyles = useMergeStyles([styles.trigger, __root != null ? __root : {}]);
    if (!state || !disclosure) {
      return null;
    }
    return /* @__PURE__ */ React5.createElement(DisclosurePrimitive, { ...state, ...restProps, ref }, ({ ...disclosureProps }) => renderClonedOutput({
      state,
      props: {
        ...disclosureProps,
        __root: triggerStyles,
        style: {}
      }
    }));
  }
);

// src/primitives/indicator.tsx
import * as React6 from "react";
import {
  systemForwardRef as systemForwardRef3,
  Base as Base3,
  useMergeStyles as useMergeStyles2,
  mergeStyles as mergeStyles2
} from "@stepstone-genesis/system";
import { ChevronTopIcon, ChevronBottomIcon } from "@stepstone-genesis/icons";
import { Motion as Motion2 } from "@stepstone-genesis/components-motion";
import { isFunction as isFunction2 } from "@stepstone-genesis/utilities";
var AnimatedToggle = ({ isVisible, animationProps, children, iconProps }) => {
  var _a;
  const { icon, __root, ...animation } = React6.useMemo(() => {
    return isFunction2(animationProps) ? animationProps({ isVisible }) : {};
  }, [animationProps, isVisible]);
  const animateIndicator = React6.useMemo(() => {
    return {
      variants: animations.indicator,
      animate: isVisible ? "visible" : "collapsed",
      initial: isVisible ? "visible" : "collapsed",
      exit: "collapsed",
      transition: {
        duration: 0.25,
        ease: [0.04, 0.62, 0.23, 0.98]
      }
    };
  }, [isVisible]);
  return /* @__PURE__ */ React6.createElement(
    Motion2,
    {
      as: "span",
      ...animateIndicator,
      __root: mergeStyles2([{ width: "iconSizeM" }, __root]),
      ...animation
    },
    (_a = children != null ? children : icon) != null ? _a : /* @__PURE__ */ React6.createElement(ChevronBottomIcon, { size: "medium", ...iconProps })
  );
};
var DisclosureIndicator = systemForwardRef3(function DisclosureIndicator2({
  isCollapsedIndicator: CollapsedIndicator = /* @__PURE__ */ React6.createElement(ChevronBottomIcon, { size: "medium" }),
  isVisibleIndicator: VisibleIndicator = /* @__PURE__ */ React6.createElement(ChevronTopIcon, { size: "medium" }),
  animationProps,
  isAnimatable = false,
  renderCustomIndicator,
  iconProps,
  __root,
  ...props
}, ref) {
  var _a, _b, _c;
  const state = useDisclosureContext();
  const custom = useCustomDisclosureContext();
  const group = useDisclosureGroupContext();
  const isIndicatorAnimatable = (_c = (_b = (_a = group.isAnimatable) != null ? _a : custom.isAnimatable) != null ? _b : animationProps) != null ? _c : isAnimatable;
  const renderAnimatableToggle = React6.useMemo(() => {
    return /* @__PURE__ */ React6.createElement(
      AnimatedToggle,
      {
        isVisible: state.visible,
        animationProps,
        iconProps
      }
    );
  }, [animationProps, state.visible, iconProps]);
  const renderVisibleCollapsed = React6.useMemo(() => {
    return state.visible ? VisibleIndicator : CollapsedIndicator;
  }, [VisibleIndicator, state.visible, CollapsedIndicator]);
  const renderChildren = React6.useMemo(() => {
    return renderCustomIndicator ? renderCustomIndicator({ isVisible: state.visible }) : void 0;
  }, [renderCustomIndicator, state.visible]);
  const indicatorStyles = useMergeStyles2([styles.indicator, __root != null ? __root : {}]);
  if (!state) {
    return null;
  }
  return /* @__PURE__ */ React6.createElement(Base3, { as: "span", __root: indicatorStyles, ...props, ref }, renderCustomIndicator ? renderChildren : isIndicatorAnimatable ? renderAnimatableToggle : renderVisibleCollapsed);
});

// src/primitives/slot.tsx
import * as React7 from "react";
var DisclosureSlot = ({
  children
}) => {
  const disclosureState = useDisclosureContext();
  const groupDisclosureState = useDisclosureGroupContext();
  const customDisclosureState = useCustomDisclosureContext();
  const state = React7.useMemo(
    () => ({
      state: disclosureState,
      groupState: groupDisclosureState,
      customState: customDisclosureState
    }),
    [disclosureState, groupDisclosureState, customDisclosureState]
  );
  return state ? children(state) : null;
};

// src/disclosure.tsx
var Disclosure = ({
  children,
  variant = "default",
  isAnimatable = false,
  ...disclosureProps
}) => {
  return /* @__PURE__ */ React8.createElement(
    DisclosureProvider,
    {
      variant,
      isAnimatable,
      ...disclosureProps
    },
    children
  );
};

// src/group-disclosure.tsx
import * as React9 from "react";
var DisclosureGroup = ({ children, isAnimatable = false, ...disclosureGroupProps }) => {
  return /* @__PURE__ */ React9.createElement(
    DisclosureGroupProvider,
    {
      isAnimatable,
      ...disclosureGroupProps
    },
    children
  );
};
export {
  Disclosure,
  DisclosureContent,
  DisclosureGroup,
  DisclosureGroupProvider,
  DisclosureIndicator,
  DisclosureProvider,
  DisclosureSlot,
  DisclosureTrigger,
  useCustomDisclosureContext,
  useDisclosureContext,
  useDisclosureGroupContext,
  useGroupDisclosure
};
